/**
 * A block of tick boxes laid out the way the printed form prints them.
 *
 * The Coast Guard 208 and 208HM both list their hazards, needs and equipment as
 * columns of checkboxes rather than as a picker, and both store the result as
 * an array of the labels that are ticked. Rendering the whole list means a
 * responder can read off the screen what they would read off the paper, and
 * that a label the storage does not recognise still shows up: `extraOptions`
 * carries anything already on the record that is not on the printed sheet, so a
 * custom hazard entered last shift is never silently dropped.
 */

import type React from 'react';
import { useId } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export type IcsCheckboxGridProps = {
  /** Names the group for screen readers; also the visible heading when shown. */
  label: string;
  /** Hides the heading for a box whose numbered title already names it. */
  hideLabel?: boolean;
  options: readonly string[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Columns at the widest breakpoint. Narrower screens collapse to one. */
  columns?: 2 | 3 | 4;
  testId?: string;
};

const COLUMN_CLASS: Record<2 | 3 | 4, string> = {
  2: 'tw:sm:grid-cols-2',
  3: 'tw:sm:grid-cols-2 tw:lg:grid-cols-3',
  4: 'tw:sm:grid-cols-2 tw:lg:grid-cols-4',
};

export const IcsCheckboxGrid: React.FC<IcsCheckboxGridProps> = ({
  label,
  hideLabel = false,
  options,
  value,
  onChange,
  disabled = false,
  columns = 3,
  testId,
}) => {
  const groupId = useId();
  // Anything on the record that the printed form has no box for, kept visible
  // so unticking it is possible.
  const extraOptions = value.filter((entry) => !options.includes(entry));
  const allOptions = [...options, ...extraOptions];

  return (
    <div
      role="group"
      aria-label={label}
      data-testid={testId}
      className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2"
    >
      {hideLabel ? null : (
        <span className="tw:text-sm tw:font-medium">{label}</span>
      )}
      <div className={`tw:grid tw:gap-2 ${COLUMN_CLASS[columns]}`}>
        {allOptions.map((option, index) => {
          // Numbered rather than named: the label is wired up through
          // `aria-labelledby`, which reads a space-separated list of ids, so a
          // box like "Hard Hat" would lose its accessible name.
          const optionId = `${groupId}-${index}`;
          const checked = value.includes(option);
          return (
            <div key={option} className="tw:flex tw:items-center tw:gap-2">
              <Checkbox
                id={optionId}
                checked={checked}
                disabled={disabled}
                onCheckedChange={(next) =>
                  onChange(
                    next
                      ? [...value, option]
                      : value.filter((entry) => entry !== option)
                  )
                }
              />
              <Label
                htmlFor={optionId}
                className="tw:text-sm tw:font-normal tw:leading-snug"
              >
                {option}
              </Label>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export type IcsYesNoRadioProps = {
  label: string;
  hideLabel?: boolean;
  /** 'yes', 'no', or '' while the question is unanswered. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  testId?: string;
};

/**
 * The Yes / No pair the printed forms give a question.
 *
 * Stored as the strings the records already hold rather than a boolean, because
 * unanswered is a third state the paper has too: neither box ticked.
 */
export const IcsYesNoRadio: React.FC<IcsYesNoRadioProps> = ({
  label,
  hideLabel = false,
  value,
  onChange,
  disabled = false,
  testId,
}) => {
  const groupId = useId();

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      {hideLabel ? null : (
        <span className="tw:text-sm tw:font-medium">{label}</span>
      )}
      <RadioGroup
        aria-label={label}
        data-testid={testId}
        disabled={disabled}
        className="tw:flex tw:gap-4"
        value={value}
        onValueChange={(next) => onChange(String(next))}
      >
        {['yes', 'no'].map((option) => (
          <div key={option} className="tw:flex tw:items-center tw:gap-2">
            <RadioGroupItem id={`${groupId}-${option}`} value={option} />
            <Label
              htmlFor={`${groupId}-${option}`}
              className="tw:text-sm tw:font-normal"
            >
              {option === 'yes' ? 'Yes' : 'No'}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
};

export default IcsCheckboxGrid;
